<script setup lang="ts">
import { RouteLocationRaw } from "vue-router";

import { useDashboardDateRange } from "@/composables/dashboard/use-dashboard-date-range";
import { useGetCurrentUserQuery } from "@/composables/kili/current-user/queries/use-get-current-user-query";
import { useGetSitesQuery } from "@/composables/kili/sites/queries/use-get-sites-query";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { V4HSiteId } from "@/data/common/ids";
import { useTypedIntl } from "@/locales/typed-intl";
import { ERouteName } from "@/router/router.type";

import ConsumptionOverviewSitesDataTable from "@/components/data/table/consumption-overview-sites-data-table.vue";
import BaseLayout from "@/components/layout/base-layout.vue";
import BaseTitle from "@/components/layout/base-title.vue";

const { TO_DAY, FROM_DAY } = useDashboardDateRange();

const MULTI = 2;
const { t, k, d } = useTypedIntl();

const { data: currentUser, isLoading: isCurrentUserLoading } = useGetCurrentUserQuery();
const { data: sites } = useGetSitesQuery();

function tableRowRouterLinkPath(siteId: V4HSiteId): RouteLocationRaw {
  return { name: ERouteName.CONSUMPTION_MONITORING_SITE_DETAILS, params: { siteId } };
}
</script>

<template>
  <BaseLayout
    class="consumption-monitoring"
    :data-test-id="DATA_TEST_IDS.views.consumptionMonitoring.view"
    :is-loading="isCurrentUserLoading"
  >
    <BaseTitle v-if="!isCurrentUserLoading">
      <template #title>
        <div
          class="flex gap-2"
          :data-test-id="DATA_TEST_IDS.views.consumptionMonitoring.title"
        >
          <h3>{{ currentUser?.organizationRootName ?? t(k.components.layout.sideMenu.consumptionMonitoring) }}</h3>
          <h3 class="text-text-secondary">
            {{ t(k.global.count.sites({ count: sites?.length ?? MULTI })) }}
          </h3>
        </div>
      </template>

      <template #subtitle>
        <span
          class="text-text-secondary"
          :data-test-id="DATA_TEST_IDS.views.consumptionMonitoring.subtitle"
        >
          {{ t(k.data.consumptionMonitoring.subtitle({ fromDay: d(FROM_DAY), toDay: d(TO_DAY) })) }}
        </span>
      </template>
    </BaseTitle>

    <ConsumptionOverviewSitesDataTable
      auto-select-single-site
      :router-link-to="tableRowRouterLinkPath"
    />
  </BaseLayout>
</template>
